import axios from 'axios'
import {
  PlunetContactOption,
  PlunetLookupItem,
  PlunetQuoteResponse,
  PlunetRequestFilterValues,
  PlunetRequestListItem,
  PlunetRequestPagination,
  PlunetRequestsPageResult,
  PlunetSelectOption,
  PlunetSubmissionPayload,
  PlunetCustomerCheckResponse,
  PlunetContactCheckResponse,
  PlunetAccountActionResult,
} from './plunet-types'

const API_URL = process.env.REACT_APP_API_URL

const normalizeLookupItem = (item: Record<string, unknown>): PlunetLookupItem => ({
  key: (item.key ?? item.Key ?? '') as string | number,
  value: String(item.value ?? item.Value ?? ''),
  label: (item.label ?? item.Label) as string | undefined,
  identification: (item.identification ?? item.Identification) as string | undefined,
})

export const toSelectOption = (item: PlunetLookupItem): PlunetSelectOption => ({
  value: String(item.key),
  label: item.label || item.value,
})

export const toSelectOptions = (rows: unknown[]): PlunetSelectOption[] =>
  rows
    .map((row) => toSelectOption(normalizeLookupItem(row as Record<string, unknown>)))
    .filter((option) => option.value && option.label)

const extractLookupRows = (response: {data?: unknown}): unknown[] => {
  const body = response?.data as Record<string, unknown> | undefined
  if (!body) {
    return []
  }

  const data = body.data ?? body.Data
  if (Array.isArray(data)) {
    return data
  }

  const nested = data as Record<string, unknown> | undefined
  if (!nested) {
    return []
  }

  const customers = nested.customers ?? nested.Customers
  const languages = nested.languages ?? nested.Languages

  if (Array.isArray(customers) || Array.isArray(languages)) {
    return []
  }

  return []
}

export const getPlunetApiErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) {
    return fallback
  }

  const status = error.response?.status
  const responseData = error.response?.data as Record<string, unknown> | undefined
  const apiError = String(responseData?.error ?? responseData?.Error ?? responseData?.title ?? '').trim()

  if (status === 404) {
    return 'Plunet API was not found on this backend. Run or deploy the latest ConnectBE build.'
  }

  if (status === 401 || status === 403) {
    return 'You are not authorized to load Plunet data. Please sign in again.'
  }

  if (apiError) {
    return apiError
  }

  if (status) {
    return `${fallback} (HTTP ${status})`
  }

  if (!API_URL) {
    return 'REACT_APP_API_URL is not configured.'
  }

  return fallback
}

// Language options use the language NAME as the option value (not the numeric Plunet ID).
// Plunet can return multiple languages that share the same numeric ID (e.g. "English (US)" and
// "Braille (English US)" can both carry id=42). Using ID as the value means the first alphabetical
// match wins in languagePlunetValue's find(), which silently sends the wrong language to the API.
// Names are unique (deduped in the backend catalog) so using name-as-value is safe and precise.
const toLanguageSelectOptions = (rows: unknown[]): PlunetSelectOption[] =>
  rows
    .map((row) => {
      const item = normalizeLookupItem(row as Record<string, unknown>)
      const label = (item.label || item.value || '').trim()
      return {value: label, label}
    })
    .filter((option) => option.value && option.label)

export const fetchPlunetLookups = async (): Promise<{
  customers: PlunetSelectOption[]
  languages: PlunetSelectOption[]
}> => {
  const response = await axios.get(`${API_URL}/plunet/lookups`)
  const body = response?.data as Record<string, unknown> | undefined
  const data = (body?.data ?? body?.Data) as Record<string, unknown> | undefined

  const customerRows = (data?.customers ?? data?.Customers ?? []) as unknown[]
  const languageRows = (data?.languages ?? data?.Languages ?? []) as unknown[]

  return {
    customers: toSelectOptions(customerRows),
    languages: toLanguageSelectOptions(languageRows),
  }
}

export const fetchPlunetCustomers = async (): Promise<PlunetSelectOption[]> => {
  const response = await axios.get(`${API_URL}/plunet/customers`)
  return toSelectOptions(extractLookupRows(response))
}

export const fetchPlunetLanguages = async (): Promise<PlunetSelectOption[]> => {
  const response = await axios.get(`${API_URL}/plunet/languages`)
  return toSelectOptions(extractLookupRows(response))
}

const normalizeContactItem = (item: Record<string, unknown>): PlunetContactOption => {
  const value = String(item.key ?? item.Key ?? '')
  const name = String(item.label ?? item.Label ?? item.value ?? item.Value ?? '').trim()
  const email = String(item.email ?? item.Email ?? '').trim()

  return {
    value,
    label: email ? `${name} (${email})` : name,
    email: email || undefined,
  }
}

export const fetchPlunetCustomerContacts = async (
  customerId: number
): Promise<PlunetContactOption[]> => {
  const response = await axios.get(`${API_URL}/plunet/customers/${customerId}/contacts`)
  const rows = response?.data?.data ?? response?.data?.Data ?? []
  if (!Array.isArray(rows)) {
    return []
  }

  return rows
    .map((row) => normalizeContactItem(row as Record<string, unknown>))
    .filter((option) => option.value && option.label)
}

const normalizeRequestListItem = (item: Record<string, unknown>): PlunetRequestListItem => ({
  requestId: Number(item.requestId ?? item.RequestId ?? 0),
  requestNumber: (item.requestNumber ?? item.RequestNumber ?? null) as string | null,
  subject: (item.subject ?? item.Subject ?? null) as string | null,
  projectName: String(item.projectName ?? item.ProjectName ?? ''),
  description: (item.description ?? item.Description ?? null) as string | null,
  customerId: (item.customerId ?? item.CustomerId ?? null) as number | null,
  customerName: (item.customerName ?? item.CustomerName ?? null) as string | null,
  status: (item.status ?? item.Status ?? null) as number | null,
  statusName: (item.statusName ?? item.StatusName ?? null) as string | null,
  creationDate: (item.creationDate ?? item.CreationDate ?? null) as string | null,
  quoteId: (item.quoteId ?? item.QuoteId ?? null) as number | null,
})

export const fetchPlunetRequestNumber = async (requestId: number): Promise<string> => {
  const response = await axios.get(`${API_URL}/plunet/requests/${requestId}/number`)
  const body = response?.data as Record<string, unknown> | undefined
  return String(body?.data ?? body?.Data ?? '').trim()
}

const normalizeRequestPagination = (payload: Record<string, unknown> | undefined): PlunetRequestPagination => {
  const pagination = (payload?.pagination ?? payload?.Pagination) as Record<string, unknown> | undefined

  return {
    page: Number(pagination?.page ?? pagination?.Page ?? 1),
    last_page: Number(pagination?.last_page ?? pagination?.Last_page ?? 0),
    items_per_page: Number(pagination?.items_per_page ?? pagination?.Items_per_page ?? 10),
    total: Number(pagination?.total ?? pagination?.Total ?? 0),
    from: Number(pagination?.from ?? pagination?.From ?? 0),
    to: Number(pagination?.to ?? pagination?.To ?? 0),
  }
}

export const fetchPlunetRequests = async (
  page = 1,
  itemsPerPage = 10,
  filters?: PlunetRequestFilterValues,
  languageOptions: PlunetSelectOption[] = [],
  refresh = false
): Promise<PlunetRequestsPageResult> => {
  const params: Record<string, string | number | boolean> = {
    page,
    items_per_page: itemsPerPage,
    refresh,
  }

  if (filters?.customerId) {
    params.customerId = Number(filters.customerId)
  }

  const sourceLanguageName = languageOptions.find((option) => option.value === filters?.sourceLanguage)?.label
  if (sourceLanguageName) {
    params.sourceLanguage = sourceLanguageName
  }

  const targetLanguageName = languageOptions.find((option) => option.value === filters?.targetLanguage)?.label
  if (targetLanguageName) {
    params.targetLanguage = targetLanguageName
  }

  if (filters?.dateFrom) {
    params.dateFrom = filters.dateFrom
  }

  if (filters?.dateTo) {
    params.dateTo = filters.dateTo
  }

  const response = await axios.get(`${API_URL}/plunet/requests`, {params})
  const body = response?.data as Record<string, unknown> | undefined
  const rows = body?.data ?? body?.Data ?? []
  const payload = (body?.payload ?? body?.Payload) as Record<string, unknown> | undefined

  const data = Array.isArray(rows)
    ? rows
        .map((row) => normalizeRequestListItem(row as Record<string, unknown>))
        .filter((item) => item.requestId > 0)
    : []

  return {
    data,
    pagination: normalizeRequestPagination(payload),
  }
}

const buildSubmissionFormData = (payload: PlunetSubmissionPayload): FormData => {
  const formData = new FormData()
  formData.append('CustomerId', String(payload.customerId))
  if (payload.customerContactId && payload.customerContactId > 0) {
    formData.append('CustomerContactId', String(payload.customerContactId))
  }
  formData.append('ProjectName', payload.projectName)
  formData.append('Description', payload.description)
  formData.append('SourceFileCount', String(payload.sourceFileCount))
  formData.append('SourceLanguage', payload.sourceLanguage)
  formData.append('TargetLanguagesCsv', payload.targetLanguages.join(','))
  formData.append('NotifyEmailsCsv', payload.notifyEmails)
  formData.append('RushRequest', payload.rushRequest ? 'true' : 'false')
  formData.append('RequestAction', String(payload.requestAction ?? 2))

  const primaryEmail = payload.notifyEmails.split(/[,;]/).map((e) => e.trim()).find(Boolean)
  if (primaryEmail) {
    formData.append('Email', primaryEmail)
  }

  payload.files.forEach((file) => {
    formData.append('Files', file)
  })

  return formData
}

export const createPlunetQuote = async (payload: PlunetSubmissionPayload): Promise<PlunetQuoteResponse> => {
  const formData = buildSubmissionFormData(payload)

  const response = await axios.post(`${API_URL}/plunet/quote`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Accept: 'application/json',
    },
  })

  return response.data
}

export const formatPlunetQuoteError = (data: Record<string, unknown> | undefined | null): string => {
  if (!data) {
    return ''
  }

  const failedStep = (data.failedStep ?? data.FailedStep) as string | undefined
  const failedApi = (data.failedApi ?? data.FailedApi) as string | undefined
  const errorDetail = (data.errorDetail ?? data.ErrorDetail ?? data.error) as string | undefined
  const statusMessage = (data.statusMessage ?? data.StatusMessage) as string | undefined

  const parts: string[] = []
  if (failedStep) {
    parts.push(`Step: ${failedStep}`)
  }
  if (failedApi) {
    parts.push(`API: ${failedApi}`)
  }
  if (errorDetail) {
    parts.push(errorDetail)
  }

  if (parts.length > 0) {
    return parts.join(' — ')
  }

  return statusMessage || ''
}

export const checkPlunetCustomer = async (
  customerId?: number,
  email?: string
): Promise<PlunetCustomerCheckResponse> => {
  const response = await axios.post(`${API_URL}/plunet/customer/check`, {
    customerId,
    email,
  })
  const body = response?.data as Record<string, unknown> | undefined
  return (body?.data ?? body?.Data ?? body) as PlunetCustomerCheckResponse
}

export const createOrLinkPlunetCustomer = async (
  customerId: number,
  connectExisting: boolean,
  plunetId?: number
): Promise<PlunetAccountActionResult> => {
  const response = await axios.post(`${API_URL}/plunet/customer/create-or-link`, {
    customerId,
    connectExisting,
    plunetId,
  })
  return response.data as PlunetAccountActionResult
}

export const checkPlunetContact = async (
  userId: string,
  email?: string
): Promise<PlunetContactCheckResponse> => {
  const response = await axios.post(`${API_URL}/plunet/contact/check`, {
    userId,
    email,
  })
  const body = response?.data as Record<string, unknown> | undefined
  return (body?.data ?? body?.Data ?? body) as PlunetContactCheckResponse
}

export const createOrLinkPlunetContact = async (
  userId: string,
  connectExisting: boolean,
  plunetContactId?: number
): Promise<PlunetAccountActionResult> => {
  const response = await axios.post(`${API_URL}/plunet/contact/create-or-link`, {
    userId,
    connectExisting,
    plunetContactId,
  })
  return response.data as PlunetAccountActionResult
}

